/**
 * El bucle de Gavi inteligente, que corre en el móvil: manda la conversación
 * al servidor (que pone la clave y llama a Claude), ejecuta aquí las
 * herramientas que pida Claude y vuelve con sus resultados hasta que conteste.
 * La conversación vive solo en la memoria de esta pantalla.
 */
import type { Pantalla, ResultadoHerramienta } from './herramientas';

/** Bloques tal como los manda y los devuelve la API (se guardan sin tocar). */
export type Bloque = {
  type: string;
  id?: string;
  name?: string;
  input?: unknown;
  text?: string;
  [clave: string]: unknown;
};

export type Mensaje = {
  role: 'user' | 'assistant';
  content: string | Bloque[];
};

export type RespuestaTurno = {
  contenido: Bloque[];
  parada: string | null;
  gasto: { mes: number; dia: number; topes: { mes: number; dia: number } };
};

export type Dependencias = {
  pedirTurno: (mensajes: Mensaje[]) => Promise<RespuestaTurno>;
  ejecutar: (
    nombre: string,
    entrada: Record<string, unknown>,
  ) => Promise<ResultadoHerramienta>;
};

/** Vueltas con herramientas por pregunta (cada una cuesta una llamada). */
export const MAX_VUELTAS = 6;
/** Por encima, se empieza una conversación nueva (el servidor acepta 40). */
export const MAX_HISTORIAL = 24;

export type Contestacion = {
  texto: string;
  pantalla?: Pantalla;
  herramientas: string[];
  /** La conversación para la próxima pregunta ([] = empezar de cero). */
  historial: Mensaje[];
  gasto?: RespuestaTurno['gasto'];
  /** Se cortó (vueltas o rechazo): la próxima pregunta empieza de cero. */
  cortada: boolean;
};

function textoDe(bloques: Bloque[]): string {
  return bloques
    .filter(b => b.type === 'text' && typeof b.text === 'string')
    .map(b => b.text as string)
    .join('\n')
    .trim();
}

export async function preguntarAGavi(
  historialPrevio: Mensaje[],
  pregunta: string,
  { pedirTurno, ejecutar }: Dependencias,
): Promise<Contestacion> {
  const base =
    historialPrevio.length >= MAX_HISTORIAL ? [] : [...historialPrevio];
  const mensajes: Mensaje[] = [...base, { role: 'user', content: pregunta }];
  const herramientas: string[] = [];
  let pantalla: Pantalla | undefined;
  let gasto: RespuestaTurno['gasto'] | undefined;

  for (let vuelta = 0; vuelta < MAX_VUELTAS; vuelta++) {
    const r = await pedirTurno([...mensajes]);
    gasto = r.gasto;
    mensajes.push({ role: 'assistant', content: r.contenido });

    if (r.parada === 'refusal') {
      return {
        texto: '',
        herramientas,
        historial: [],
        gasto,
        cortada: true,
      };
    }

    const llamadas = r.contenido.filter(b => b.type === 'tool_use');
    if (r.parada !== 'tool_use' || llamadas.length === 0) {
      return {
        texto: textoDe(r.contenido),
        pantalla,
        herramientas,
        historial: mensajes,
        gasto,
        cortada: false,
      };
    }

    const resultados: Bloque[] = [];
    for (const l of llamadas) {
      const nombre = String(l.name);
      herramientas.push(nombre);
      const res = await ejecutar(
        nombre,
        (l.input ?? {}) as Record<string, unknown>,
      );
      if (res.pantalla) {
        pantalla = res.pantalla;
      }
      resultados.push({
        type: 'tool_result',
        tool_use_id: l.id,
        content: res.contenido,
        ...(res.esError ? { is_error: true } : {}),
      });
    }
    mensajes.push({ role: 'user', content: resultados });
  }

  return {
    texto: '',
    pantalla,
    herramientas,
    historial: [],
    gasto,
    cortada: true,
  };
}
